import React, { useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { Box, Alert } from '@mui/material'
import { styled } from '@mui/material/styles'
import { httpsCallable } from 'firebase/functions'
import firebaseService from '../firebase-service'
import { brandColors, brandFonts } from '../styled/brand'
import { Wrap } from '../components/brand/Wrap'
import Kicker from '../components/brand/Kicker'

const sendRequest = httpsCallable<{ email: string; recaptcha: string }, void>(
  firebaseService.functions,
  'accountDelete'
)

const FieldLabel = styled('span')({
  fontFamily: brandFonts.heading,
  fontWeight: 600,
  fontSize: 13,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: brandColors.nearBlack2,
})

const EmailInput = styled('input')({
  fontFamily: brandFonts.body,
  fontSize: 16,
  padding: '14px 18px',
  borderRadius: 10,
  border: `1px solid rgba(31,29,27,0.16)`,
  background: '#fff',
  color: brandColors.nearBlack2,
  outline: 'none',
  width: '100%',
  '&:focus': { borderColor: brandColors.coralText },
})

const SubmitButton = styled('button')({
  fontFamily: brandFonts.heading,
  fontWeight: 700,
  fontSize: 15,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: brandColors.cream,
  background: brandColors.nearBlack,
  border: 'none',
  borderRadius: 10,
  padding: 18,
  cursor: 'pointer',
  '&:disabled': { opacity: 0.6, cursor: 'default' },
})

const disclosures = [
  "You're requesting deletion of your account and all associated data.",
  "We'll send a verification email to the address above.",
  "You'll need access to that email to complete the process.",
]

const AccountDeletionRequest: React.FC = () => {
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<boolean>(false)
  const [captchaValue, setCaptchaValue] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!captchaValue) {
      setError('Please complete the reCAPTCHA.')
      return
    }

    try {
      setLoading(true)
      await sendRequest({ email, recaptcha: captchaValue })
      setSuccess(true)
      setEmail('')
    } catch (error) {
      setError((error as any).message || 'An error occurred')
      console.error('Error submitting request:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ background: brandColors.cream, minHeight: '100vh' }}>
      <Box sx={{ padding: '40px 0 100px' }}>
        <Wrap>
          <Kicker align="center">Account</Kicker>
          <Box
            component="h1"
            sx={{
              margin: '20px 0 0',
              fontFamily: brandFonts.heading,
              fontWeight: 700,
              fontSize: { xs: 42, md: 48 },
              lineHeight: 1.08,
              letterSpacing: '-0.02em',
              color: brandColors.nearBlack2,
              textAlign: 'center',
            }}
          >
            Delete your account.
          </Box>
          <Box
            component="p"
            sx={{
              margin: '20px auto 0',
              fontSize: 18,
              lineHeight: 1.6,
              fontWeight: 500,
              color: brandColors.textMuted,
              maxWidth: 520,
              textAlign: 'center',
            }}
          >
            This permanently removes your account and all associated data. We'll
            email you to confirm before anything happens.
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              maxWidth: 620,
              mx: 'auto',
              mt: '56px',
              background: brandColors.creamLight,
              borderRadius: '20px',
              padding: { xs: '32px', md: '48px' },
              display: 'flex',
              flexDirection: 'column',
              gap: '28px',
            }}
          >
            {/* iOS users callout */}
            <Box
              sx={{
                background: brandColors.cream,
                borderRadius: '12px',
                padding: '20px 24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: brandFonts.heading,
                  fontWeight: 700,
                  fontSize: 14,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: brandColors.coralText,
                }}
              >
                iOS users
              </Box>
              <Box
                component="span"
                sx={{ fontSize: 15, lineHeight: 1.6, color: '#3A2E2A', fontWeight: 500 }}
              >
                If you used "Sign in with Apple" and chose not to share your
                email, enter the anonymized address instead — find it in the
                app's Settings tab or your{' '}
                <a
                  href="https://support.apple.com/en-ca/102571"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: brandColors.coralText }}
                >
                  Apple ID settings
                </a>
                .
              </Box>
            </Box>

            {/* Email */}
            <Box component="label" sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <FieldLabel>Account email address</FieldLabel>
              <EmailInput
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Box>

            {/* reCAPTCHA */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <FieldLabel>Verify you're human</FieldLabel>
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                onChange={(value: any) => setCaptchaValue(value)}
              />
            </Box>

            {/* Disclosures */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                fontSize: 15,
                lineHeight: 1.6,
                fontWeight: 500,
                color: '#3A2E2A',
              }}
            >
              <span>By submitting this form:</span>
              {disclosures.map((text, i) => (
                <Box key={i} sx={{ display: 'flex', gap: '10px' }}>
                  <Box component="span" sx={{ color: brandColors.coralText, fontWeight: 700 }}>
                    {i + 1}.
                  </Box>
                  <span>{text}</span>
                </Box>
              ))}
            </Box>

            <SubmitButton type="submit" disabled={loading}>
              {loading ? 'Sending…' : 'Submit request'}
            </SubmitButton>

            {error && <Alert severity="error">{error}</Alert>}
            {success && (
              <Alert severity="success">
                Your delete account request has been submitted. The processing of
                your request may take up to a few days. You will receive an email
                with further instructions.
              </Alert>
            )}
          </Box>
        </Wrap>
      </Box>
    </Box>
  )
}

export default AccountDeletionRequest
